// MARK://dhk8/admin-shell/pages/dev/catalog-shell
// Mini-framework propio del catálogo en vivo (sin Storybook). Registro de
// secciones + sidebar por categorías + render perezoso (solo la sección activa
// se construye) + reset de scroll al cambiar. Ver brands/nintaigo/docs/design-system.md §5.
//
// DUPLICADO intencional con apps/pos/src/pages/dev/catalog-shell.tsx: hoy
// @dockethub/ui-restaurant (el único paquete compartido por ambas apps) compila
// a `dist/` vía su propio `tsc -p tsconfig.json` — consumirlo en vivo desde un
// catálogo dev exigiría un paso de build extra en cada iteración. Un archivo
// idéntico por app es más simple y más seguro; DS-4 lo hoistea cuando la unificación
// de primitivos ya esté decidida.
import { useEffect, useRef, useState, type ReactNode } from 'react'

export interface CatalogSection {
  id: string
  label: string
  category: string
  render: () => ReactNode
}

export function CatalogShell({
  title,
  subtitle,
  sections,
}: {
  title: string
  subtitle?: string
  sections: CatalogSection[]
}) {
  const categories: string[] = []
  for (const s of sections) if (!categories.includes(s.category)) categories.push(s.category)

  const [activeId, setActiveId] = useState(sections[0]?.id ?? '')
  const active = sections.find((s) => s.id === activeId) ?? sections[0]
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => { contentRef.current?.scrollTo({ top: 0 }) }, [active?.id])

  return (
    <div className="adm-page cat-shell">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">{title}</h1>
          {subtitle && <p className="adm-page__sub">{subtitle}</p>}
        </div>
      </div>

      <div className="cat-shell__body">
        <nav className="cat-shell__sidebar" aria-label="Categorías del catálogo">
          {categories.map((cat) => (
            <div key={cat} className="cat-shell__group">
              <div className="cat-shell__group-label">{cat}</div>
              {sections.filter((s) => s.category === cat).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`cat-shell__item${s.id === active?.id ? ' cat-shell__item--active' : ''}`}
                  aria-current={s.id === active?.id ? 'page' : undefined}
                  onClick={() => setActiveId(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* `key` fuerza remount al cambiar de sección — cada componente arranca con
            estado limpio y solo la sección visible se construye (render perezoso). */}
        <div className="cat-shell__content" ref={contentRef} key={active?.id}>
          {active ? active.render() : <p className="adm-muted">Sin secciones registradas.</p>}
        </div>
      </div>

      <style>{catalogShellStyles}</style>
    </div>
  )
}

/** Card que agrupa un componente + todas sus variantes/estados. */
export function CatalogBlock({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <div className="adm-card adm-card--pad cat-block">
      <div className="cat-block__head">
        <div className="adm-card__title">{title}</div>
        {note && <div className="cat-block__note">{note}</div>}
      </div>
      <div className="cat-block__body">{children}</div>
    </div>
  )
}

/** Sub-agrupación DENTRO de un bloque (ej. por `tone`/`size`) con subtítulo. */
export function CatalogGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="cat-group">
      {label && <div className="cat-group__label">{label}</div>}
      <div className="cat-group__row">{children}</div>
    </div>
  )
}

const catalogShellStyles = `
  .cat-shell__body { display: flex; gap: var(--pos-space-4, 16px); align-items: flex-start; min-height: 0; }
  .cat-shell__sidebar {
    flex-shrink: 0; width: 208px; display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
    position: sticky; top: 0; max-height: calc(100dvh - 160px); overflow-y: auto;
  }
  .cat-shell__group { display: flex; flex-direction: column; gap: 2px; }
  .cat-shell__group-label {
    padding: 0 var(--pos-space-2, 8px); font-size: var(--pos-text-xxs, 11px); font-weight: var(--pos-weight-semibold, 600);
    letter-spacing: var(--pos-tracking-wide, .04em); text-transform: uppercase; color: var(--pos-fg-tertiary);
  }
  .cat-shell__item {
    text-align: left; min-height: 36px; padding: 0 var(--pos-space-2, 8px); border: none; border-radius: var(--pos-radius-md, 8px);
    background: transparent; color: var(--pos-fg-secondary); font: inherit; font-size: var(--pos-text-sm, 13px); cursor: pointer;
    transition: background var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease), color var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease);
  }
  .cat-shell__item:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .cat-shell__item--active { background: var(--pos-tint-fill); color: var(--pos-tint-on, var(--pos-fg)); font-weight: var(--pos-weight-semibold, 600); }
  .cat-shell__content { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: var(--pos-space-4, 16px); }

  .cat-block__head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--pos-space-3, 12px); flex-wrap: wrap; }
  .cat-block__note { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); }
  .cat-block__body { display: flex; flex-direction: column; gap: var(--pos-space-4, 16px); margin-top: var(--pos-space-2, 8px); }

  .cat-group { display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); }
  .cat-group__label { font-size: var(--pos-text-xxs, 11px); font-weight: var(--pos-weight-medium, 500); text-transform: uppercase; letter-spacing: var(--pos-tracking-wide, .04em); color: var(--pos-fg-tertiary); }
  .cat-group__row { display: flex; flex-wrap: wrap; gap: var(--pos-space-3, 12px); align-items: flex-start; }

  @media (max-width: 720px) {
    .cat-shell__body { flex-direction: column; }
    .cat-shell__sidebar { width: 100%; position: static; max-height: none; flex-direction: row; flex-wrap: wrap; }
  }
`
